import React from 'react';
import { Helmet } from 'react-helmet';
import { MapPin, Clock, Users, Mountain } from 'lucide-react';
import Header from '../../components/ui/Header';

const TrailDetailsZivotKrozVekoveGolija = () => {
  return (
    <>
      <Helmet>
        <title>Život kroz vekove – Stanovništvo Golije - Creative Twinning</title>
        <meta
          name="description"
          content="Otkrijte planinsku stazu kroz Goliju gde vreme teče sporije. Prošetajte kroz sela Rudno i Biniće, upoznajte se sa tradicionalnim načinom života i doživite mir planine." />

        <meta name="keywords" content="Golija, planina, Srbija, kulturno nasleđe, tradicionalni život, Studenica, Gradac" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section */}
          <div className="relative h-96 bg-cover bg-center" style={{ backgroundImage: `url('https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763506392/Golija_lx2nma.jpg')` }}>
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <h1 className="text-4xl md:text-6xl font-bold mb-4">
                  Život kroz vekove – Stanovništvo Golije
                </h1>
                <p className="text-xl md:text-2xl">
                  Staza kroz vreme i tradiciju planine Golije
                </p>
              </div>
            </div>
          </div>

          {/* Trail Info */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <MapPin className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Dužina</h3>
                <p className="text-gray-600">9 km</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Clock className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Trajanje</h3>
                <p className="text-gray-600">oko 3 sata</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Mountain className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Težina</h3>
                <p className="text-gray-600">Laka do srednja</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Users className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Za koga</h3>
                <p className="text-gray-600">Sve uzraste</p>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">O stazi</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Na obroncima Golije, planine koja diše u ritmu tišine, vreme teče sporije i mirnije nego igde drugde. 
                U selima Rudno i Biniće, između mirisa dima i hleba pečenog pod sačem, i danas se živi gotovo isto kao pre stotinu godina. 
                Staza „Život kroz vekove – Stanovništvo Golije" vodi kroz svakodnevicu ovih planinskih sela – kroz njihove šume, livade, izvore i priče.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Svaki korak na ovoj ruti otkriva jednostavnost, dostojanstvo i toplinu ljudi koji su naučili da slušaju planinu. 
                Ovo nije samo šetnja kroz prirodu, već i kroz vreme.
              </p>
            </div>

            {/* Natural Attractions */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Prirodne atrakcije</h2>
              
              {/* Brevina reka */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Brevina reka – planinska oaza</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Na samo nekoliko kilometara od Rudna, Brevina reka vijuga kroz šumu i stvara male virove u kojima se reflektuje nebo. 
                      Ovde meštani dolaze na izlet, a putnici da predahnu i oslušnu tišinu koju prekida samo šum vode.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      U blizini reke raste lekovito bilje – kantarion, majčina dušica, nana i planinski čaj, pa nije retkost 
                      sresti žene koje beru travke, kao što su to činile i njihove bake.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763406768/REKA_BREVINA_1.2-_PRIRODNE_ATRAKCIJE_a2st2b.jpg"
                      alt="Brevina reka koja vijuga kroz planinski predeo sa čistom vodom i zelenilom oko nje"
                      className="w-full h-64 object-cover" />

                  </div>
                </div>
              </div>

              {/* Košaninova jezera */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Košaninova jezera – čudo prirode</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763406767/1.3-KOSANINOVA_JEZERA_tmsfie.jpg"
                      alt="Košaninova jezera okružena livadama i borovom šumom na planini Goliji"
                      className="w-full h-64 object-cover" />

                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Na oko 900 metara nadmorske visine, između livada i borove šume, nalaze se Košaninova jezera – mala i velika. 
                      Fenomen ovog mesta je što se nivo vode menja suprotno prirodnim zakonima: kad pada kiša, jezera se povlače, a tokom suše se pune.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Legenda kaže da svaka kap dolazi iz podzemnih izvora koji „dišu" zajedno sa planinom. 
                      U predvečerje, površina bara presijava se u svim bojama neba – prizor koji ostaje zauvek u sećanju.
                    </p>
                  </div>
                </div>
              </div>

              {/* Izvori reke Izubre */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Izvori reke Izubre – tajna u šumi</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <div className="flex items-center mb-2 text-gray-600">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>GPS: 43°26'06"N 20°25'40"E</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Na severnim padinama Golije, voda Izubre izbija iz stena i formira pet manjih vodopada. 
                      Put do izvora prolazi kroz šumu bukve i breze, gde se čuju samo ptice i voda.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Kažu da je to najlepši skriveni kutak Golije – mesto gde priroda šapuće.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763406768/vodopadi_Izubre_1.3.1_ocjre8.jpg"
                      alt="Vodopadi reke Izubre koji se spuštaju kroz šumovite stene na planini Goliji"
                      className="w-full h-64 object-cover" />

                  </div>
                </div>
              </div>
            </div>

            {/* Cultural Heritage */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Kulturno nasleđe</h2>
              
              {/* Manastir Studenica */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Manastir Studenica – biser srpske duhovnosti</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763453391/1.4_aeaygw.jpg"
                      alt="Manastir Studenica sa belim mermernim zidovima i kupola okružen zelenilom"
                      className="w-full h-64 object-cover" />

                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Na samo petnaest kilometara od staze, u dolini reke Studenice, uzdiže se manastir koji vekovima čuva srpsku istoriju i veru. 
                      Zadužbina Stefana Nemanje, osnivača srpske države, izgrađena krajem XII veka, jedno je od najlepših dela srpske srednjovekovne umetnosti.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Manastir je pod zaštitom UNESCO-a i predstavlja središte molitve, tišine i svetlosti. 
                      Beli mermer njegove fasade i plavetnilo freske „Raspeće Hristovo" predstavljaju ne samo umetnost, već i duboku duhovnost.
                    </p>
                  </div>
                </div>
              </div>

              {/* Manastir Gradac */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Manastir Gradac – miris jorgovana i sećanja</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Na samo pet kilometara od Rudna nalazi se manastir Gradac, zadužbina kraljice Jelene Anžujske, 
                      žene koja je Srbima donela dah Zapada i legendu o ljubavi.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Kažu da su zbog nje duž Ibra posađeni jorgovani, da je podsete na Provanse — i tako je nastala Dolina jorgovana, 
                      simbol večnog spoja Istoka i Zapada. Gradac je danas mesto tišine, duhovnosti i ženske snage.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763453392/1.5_3_i9smyb.jpg"
                      alt="Manastir Gradac sa kamenim zidovima u prirodnom okruženju planine Golije"
                      className="w-full h-64 object-cover" />

                  </div>
                </div>
              </div>

              {/* Crkva Svetog Nikole – Nikoljača */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Crkva Svetog Nikole – Nikoljača</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763453424/NIKOLJACA_1.7_qo3os9.jpg"
                      alt="Crkva Svetog Nikole Nikoljača sagrađena od kamena i drveta sa malim tremom okružena šumom"
                      className="w-full h-64 object-cover" />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Na svega dva kilometra od centra Rudna, skrivena u šumi, stoji crkva Svetog Nikole – Nikoljača, 
                      jedna od manastirskih bogomolja Studeničkog kraja. Sagrađena od kamena i drveta, sa malim tremom, 
                      pruža pogled na šumu i nebo.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Mesto je duhovnog predaha, gde su reči suvišne, a tišina govori. 
                      Okružena visokim borovinama, ova mala crkva predstavlja savršen spoj prirode i duhovnosti.
                    </p>
                  </div>
                </div>
              </div>

              {/* Crkva Svete Trojice */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Crkva Svete Trojice – molitva u tišini</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763453408/CRKVA_SV._TROJICE_-RUDNO_BUNAR_1.6izvor_studenica_info_hvryl9.jpg"
                      alt="Mala crkva Svete Trojice okružena visokim stablima u selu Rudno"
                      className="w-full h-64 object-cover" />

                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      U zaseoku Bunar, na obodu Rudna, nalazi se mala crkva Svete Trojice. 
                      Okružena starim stablima, izgleda kao da je izrasla iz zemlje zajedno sa njima.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Meštani je posećuju o praznicima, a putnici zastaju da zapale sveću i ostave želju. 
                      U njenoj jednostavnosti leži mir, a u zvuku zvona – sećanje na davne dane kada su se molitve uzdizale sa svakog brda.
                    </p>
                  </div>
                </div>
              </div>

              {/* Tradicionalne kuće */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Tradicionalne kuće – čuvari starine</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      U okolnim selima još uvek se mogu videti stare brvnare pokrivene slamom i kamene kuće sa niskim pragovima. 
                      U njima vreme stoji, a zidovi mirišu na dim i drvo.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Neke su obnovljene i pretvorene u seoska domaćinstva, ali zadržale su dušu prošlih vremena. 
                      Svaka kuća ima priču, i svaka priča – svoje ognjište.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763453422/kuca-vilimonovica-rudno-TRADICIONALNA_KUCA_1.8_jkyznx.jpg"
                      alt="Tradicionalna kamena kuća sa slamenim krovom u selu Rudno na planini Goliji"
                      className="w-full h-64 object-cover" />

                  </div>
                </div>
              </div>
            </div>

            {/* Itinerary */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Predlog programa posete</h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Osnovne informacije</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Dužina staze:</strong> 9 km</li>
                    <li><strong>Trajanje:</strong> oko 3 sata laganog hoda</li>
                    <li><strong>Težina:</strong> laka do srednje zahtevna</li>
                    <li><strong>Preporučeno za:</strong> planinare, porodice, bicikliste</li>
                    <li><strong>Najbolje vreme:</strong> april–oktobar</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Predlog obilaska</h3>
                  <ol className="space-y-2 text-gray-700 list-decimal list-inside">
                    <li>Početak šetnje u centru Rudna (950 m n.v.)</li>
                    <li>Uspon do Veleňske Gradine</li>
                    <li>Odmor kod izvora Vrela</li>
                    <li>Obilazak crkve Svete Trojice</li>
                    <li>Povratak u Rudno kružnim putem</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Don't Miss Section */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Ne propustite</h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Pogled sa Veleňske Gradine</h3>
                  <p className="text-gray-700 text-sm">
                    Nezaboravan pogled na čitavu Goliju i okolne planine.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Izvor Vrela i miris planinskih trava</h3>
                  <p className="text-gray-700 text-sm">
                    Čista planinska voda i autohtono lekovito bilje.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Manastire Studenicu i Gradac (UNESCO)</h3>
                  <p className="text-gray-700 text-sm">
                    Dva svetska kulturna spomenika u neposrednoj blizini.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Vodopade Izubre i Košaninove bare</h3>
                  <p className="text-gray-700 text-sm">
                    Jedinstveni prirodni fenomeni Golije.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Domaću hranu u seoskim domaćinstvima Rudna</h3>
                  <p className="text-gray-700 text-sm">
                    Tradiconalna jela pripremljena po starim receptima.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Manifestacije „Radočelski sabor" i „Susreti Golijana"</h3>
                  <p className="text-gray-700 text-sm">
                    Tradicionalne manifestacije koje čuvaju kulturno nasleđe.
                  </p>
                </div>
              </div>
            </div>

            {/* Accommodation */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Gde jesti i prespavati</h2>
              
              {/* Restaurants Section */}
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Restorani</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <img
                        src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763455562/1.9-a-c.jpg_mini_gthy9i.jpg"
                        alt="Restoran Beli Bagrem sa tradicionalnim ambijentom i drenim terasama"
                        className="w-full h-48 object-cover rounded-lg" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Restoran Beli Bagrem</h4>
                    <div className="space-y-1 text-gray-600 mb-3">
                      <p>📍 Rudno 27, 36222 Kraljevo</p>
                      <p>📞 +381 36 5437111</p>
                    </div>
                    <p className="text-gray-700">
                      Porodični ambijent sa tradicionalnom kuhinjom. Specijaliteti su jela od heljde, krompira, sira i jagnjetine.
                    </p>
                  </div>
                  
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <img
                        src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763455563/1.10A-C.jpg_mini_znckdk.jpg"
                        alt="Restoran Vidovdan sa toplim ambijentom za susrete meštana i putnika"
                        className="w-full h-48 object-cover rounded-lg" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Restoran Vidovdan</h4>
                    <div className="space-y-1 text-gray-600 mb-3">
                      <p>📍 Rudno 41, 36222 Kraljevo</p>
                      <p>📞 +381 64 1892474</p>
                    </div>
                    <p className="text-gray-700">
                      Mesto susreta meštana i putnika sa tradicionalnim jelima od lokalnih proizvoda.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Accommodation Section */}
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Smeštaj</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <img
                        src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763455566/1.11a.jpg_mini_ig2oun.jpg"
                        alt="Domaćinstvo Šekler sa drvenim kućama u prirodnom okruženju"
                        className="w-full h-48 object-cover rounded-lg" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Domaćinstvo Šekler</h4>
                    <div className="space-y-1 text-gray-600 mb-3">
                      <p>📍 Rudno 27, 36222 Kraljevo</p>
                      <p>📞 +381 64 1276977</p>
                    </div>
                    <p className="text-gray-700 text-sm">
                      Šest drvenih kuća, domaća kuhinja, radionice o bilju i vožnja kvadovima.
                    </p>
                  </div>
                  
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <img
                        src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763455808/1.12a-c.jpg_mini_prtci4.jpg"
                        alt="Pansion Nebo sa drvenim kućama na visokoj nadmorskoj visini"
                        className="w-full h-48 object-cover rounded-lg" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Pansion Nebo</h4>
                    <div className="space-y-1 text-gray-600 mb-3">
                      <p>📍 Srnjača, Rudno, 36222 Kraljevo</p>
                      <p>📞 +381 64 1173262</p>
                    </div>
                    <p className="text-gray-700 text-sm">
                      Na visini od 1.250 m, drvene kuće sa pogledom na pašnjake i planinu.
                    </p>
                  </div>
                  
                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <img
                        src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763455811/1.13a-c.jpg_mini_lcuhqe.jpg"
                        alt="Vila Selena gde umetnost i priroda žive zajedno"
                        className="w-full h-48 object-cover rounded-lg" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Vila Selena</h4>
                    <div className="space-y-1 text-gray-600 mb-3">
                      <p>📍 Rudno 48, 36222 Kraljevo</p>
                      <p>📞 +381 64 1900851</p>
                    </div>
                    <p className="text-gray-700 text-sm">
                      Kuća gde umetnost i priroda žive zajedno. Organizuje se kolonija „Golija–Rudno".
                    </p>
                  </div>

                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <img
                        src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763455813/1.14a-c.jpg_mini_gzz6au.jpg"
                        alt="Bajkovito Selo sa tri drvene kućice među brežuljcima sa đakuzijem"
                        className="w-full h-48 object-cover rounded-lg" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Bajkovito Selo</h4>
                    <div className="space-y-1 text-gray-600 mb-3">
                      <p>📍 Rudno, 36222 Kraljevo</p>
                      <p>📞 +381 63 1611115</p>
                      <p>📍 GPS: 43°24'55"N 20°28'58"E</p>
                    </div>
                    <p className="text-gray-700 text-sm">
                      Tri drvene kućice skrivene među brežuljcima, dvorište puno cveća i drveni đakuzi pod otvorenim nebom. 
                      Sve deluje kao iz bajke, osim što je stvarno.
                    </p>
                  </div>

                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <img
                        src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763455574/1.16.jpg_mini_fxgs2f.jpg"
                        alt="Vila Jela na ivici livade sa pogledom na planinu Goliju"
                        className="w-full h-48 object-cover rounded-lg" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Vila Jela</h4>
                    <div className="space-y-1 text-gray-600 mb-3">
                      <p>📍 Rudno 41, 36222 Kraljevo</p>
                      <p>📞 +381 65 451 9912</p>
                      <p>✉️ vilajela@gmail.com</p>
                      <p>📍 GPS: 43°24'57"N 20°28'57"E</p>
                    </div>
                    <p className="text-gray-700 text-sm">
                      Smeštena na ivici livade, vila „Jela" pruža savršen mir. Prozori gledaju na Goliju, 
                      a domaćini dočekuju goste uz domaći sir, med i toplu supu.
                    </p>
                  </div>

                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <img
                        src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763455576/1.17.jpg_mini_duylp5.jpg"
                        alt="Vila Maja sa velikim tremom i pogledom na pašnjake"
                        className="w-full h-48 object-cover rounded-lg" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Vila Maja</h4>
                    <div className="space-y-1 text-gray-600 mb-3">
                      <p>📍 Rudno 19, 36222 Kraljevo</p>
                      <p>📞 +381 64 191 7642</p>
                      <p>✉️ kontakt@vilamaja.rs</p>
                      <p>📍 GPS: 43°24'49"N 20°29'07"E</p>
                    </div>
                    <p className="text-gray-700 text-sm">
                      U „Vili Maja" sve miriše na bor i domaći hleb. Kuća ima veliki trem sa pogledom na pašnjake, 
                      a svaka soba odiše toplinom i mirom.
                    </p>
                  </div>

                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <img
                        src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763455581/1.18_qvnofi.jpg"
                        alt="Planinski predah sa drvenim brvnarama okruženim šumom"
                        className="w-full h-48 object-cover rounded-lg" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Planinski predah</h4>
                    <div className="space-y-1 text-gray-600 mb-3">
                      <p>📍 Rudno bb, 36222 Kraljevo</p>
                      <p>📞 +381 63 112 0405</p>
                      <p>✉️ planinskipredah@gmail.com</p>
                      <p>📍 GPS: 43°24'54"N 20°28'52"E</p>
                    </div>
                    <p className="text-gray-700 text-sm">
                      Drvene brvnare u srcu planine, okružene šumom i livadama. Mir, tišina i zvuk potoka 
                      stvaraju osećaj potpune slobode.
                    </p>
                  </div>

                  <div className="p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <img
                        src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763455583/1.19_hxywoa.jpg"
                        alt="Orlovo gnezdo na litici sa pogledom na čitavu Goliju"
                        className="w-full h-48 object-cover rounded-lg" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Orlovo gnezdo</h4>
                    <div className="space-y-1 text-gray-600 mb-3">
                      <p>📍 Rudno bb, 36222 Kraljevo</p>
                      <p>📞 +381 64 556 2228</p>
                      <p>✉️ orlovognezdo.rudno@gmail.com</p>
                      <p>📍 GPS: 43°25'02"N 20°29'15"E</p>
                    </div>
                    <p className="text-gray-700 text-sm">
                      Na litici iznad doline, „Orlovo gnezdo" opravdava svoje ime. Pogled sa terase obuhvata čitavu Goliju, 
                      a noći su tihe, s vetrom koji nosi miris borova.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default TrailDetailsZivotKrozVekoveGolija;